import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion } from 'framer-motion';

// Game constants that adapt to screen size
const getGameDimensions = () => {
  const isMobile = window.innerWidth < 768;
  return {
    GAME_HEIGHT: isMobile ? 450 : 650,
    GAME_WIDTH: isMobile ? Math.min(window.innerWidth - 30, 450) : 900,
    HANUMAN_WIDTH: isMobile ? 80 : 100,
    HANUMAN_HEIGHT: isMobile ? 100 : 120,
    OBSTACLE_WIDTH: isMobile ? 60 : 80,
    OBSTACLE_HEIGHT: isMobile ? 60 : 80,
    SANJEEVANI_WIDTH: isMobile ? 40 : 50,
    SANJEEVANI_HEIGHT: isMobile ? 40 : 50
  };
};

const HanumanLeapChallenge = () => {
  // Get initial dimensions
  const initialDimensions = getGameDimensions();
  
  const [gameState, setGameState] = useState('menu'); // menu, playing, gameOver
  const [gameDimensions, setGameDimensions] = useState(initialDimensions);
  const [hanumanPosition, setHanumanPosition] = useState({ 
    x: initialDimensions.GAME_WIDTH * 0.1, 
    y: initialDimensions.GAME_HEIGHT * 0.5 
  });
  const [velocity, setVelocity] = useState(0);
  const [obstacles, setObstacles] = useState([]);
  const [sanjeevaniHerbs, setSanjeevaniHerbs] = useState([]);
  const [score, setScore] = useState(0);
  const [highScore, setHighScore] = useState(localStorage.getItem('hanumanHighScore') || 0);
  const [isJumping, setIsJumping] = useState(false);
  const [lives, setLives] = useState(3);
  const [scoreAnimation, setScoreAnimation] = useState(false);
  
  const gameLoopRef = useRef();
  const obstacleSpawnRef = useRef();
  const herbSpawnRef = useRef();
  const lastTimeRef = useRef();
  const jumpCooldownRef = useRef(false);
  const audioContextRef = useRef(null);

  // Handle window resize for responsive design
  useEffect(() => {
    const handleResize = () => {
      const dimensions = getGameDimensions();
      setGameDimensions(dimensions);
      
      // Adjust Hanuman position proportionally
      setHanumanPosition(prev => ({
        x: (prev.x / gameDimensions.GAME_WIDTH) * dimensions.GAME_WIDTH || dimensions.GAME_WIDTH * 0.1,
        y: (prev.y / gameDimensions.GAME_HEIGHT) * dimensions.GAME_HEIGHT || dimensions.GAME_HEIGHT * 0.5
      }));
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [gameDimensions]);

  // Initialize audio context
  const initAudio = () => {
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
  };

  // Play sound effects using Web Audio API
  const playSound = (type) => {
    initAudio();
    const audioContext = audioContextRef.current;
    
    if (!audioContext) return;
    
    try {
      switch (type) {
        case 'jump':
          // Create a simple beep sound for jump
          const jumpOscillator = audioContext.createOscillator();
          const jumpGain = audioContext.createGain();
          
          jumpOscillator.connect(jumpGain);
          jumpGain.connect(audioContext.destination);
          
          jumpOscillator.type = 'sine';
          jumpOscillator.frequency.setValueAtTime(523.25, audioContext.currentTime); // C5
          jumpGain.gain.setValueAtTime(0.3, audioContext.currentTime);
          jumpGain.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.2);
          
          jumpOscillator.start(audioContext.currentTime);
          jumpOscillator.stop(audioContext.currentTime + 0.2);
          break;
          
        case 'collect':
          // Create a pleasant sound for collecting herbs
          const collectOscillator = audioContext.createOscillator();
          const collectGain = audioContext.createGain();
          
          collectOscillator.connect(collectGain);
          collectGain.connect(audioContext.destination);
          
          collectOscillator.type = 'sine';
          collectOscillator.frequency.setValueAtTime(783.99, audioContext.currentTime); // G5
          collectGain.gain.setValueAtTime(0.3, audioContext.currentTime);
          collectGain.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);
          
          collectOscillator.start(audioContext.currentTime);
          collectOscillator.stop(audioContext.currentTime + 0.3);
          break;
          
        case 'hit':
          // Create a sound for hitting obstacles
          const hitOscillator = audioContext.createOscillator();
          const hitGain = audioContext.createGain();
          
          hitOscillator.connect(hitGain);
          hitGain.connect(audioContext.destination);
          
          hitOscillator.type = 'square';
          hitOscillator.frequency.setValueAtTime(220, audioContext.currentTime); // A3
          hitGain.gain.setValueAtTime(0.3, audioContext.currentTime);
          hitGain.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);
          
          hitOscillator.start(audioContext.currentTime);
          hitOscillator.stop(audioContext.currentTime + 0.3);
          break;
          
        case 'gameOver':
          // Create a game over sound
          const gameOverOscillator = audioContext.createOscillator();
          const gameOverGain = audioContext.createGain();
          
          gameOverOscillator.connect(gameOverGain);
          gameOverGain.connect(audioContext.destination);
          
          gameOverOscillator.type = 'sine';
          gameOverOscillator.frequency.setValueAtTime(174.61, audioContext.currentTime); // F3
          gameOverGain.gain.setValueAtTime(0.3, audioContext.currentTime);
          gameOverGain.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 1.0);
          
          gameOverOscillator.start(audioContext.currentTime);
          gameOverOscillator.stop(audioContext.currentTime + 1.0);
          break;
          
        default:
          break;
      }
    } catch (error) {
      console.log('Sound error:', error);
    }
  };

  // Handle jump - improved for smoother experience
  const jump = useCallback(() => {
    if (gameState === 'playing') {
      // Reset jump cooldown
      if (jumpCooldownRef.current) return;
      
      jumpCooldownRef.current = true;
      setTimeout(() => {
        jumpCooldownRef.current = false;
      }, 80); // Shorter cooldown for more responsive feel
      
      setVelocity(prevVelocity => {
        // More responsive jumping with variable jump force
        let jumpForce;
        
        // If falling fast, give a stronger jump
        if (prevVelocity > 10) {
          jumpForce = -16;
        } 
        // If falling slowly or moving up, give a normal jump
        else if (prevVelocity > -5) {
          jumpForce = -13;
        } 
        // If moving up quickly, give a small boost
        else {
          jumpForce = -7;
        }
        
        const newVelocity = prevVelocity + jumpForce;
        return Math.max(newVelocity, -25); // Cap upward velocity
      });
      
      // Visual jump effect
      setIsJumping(true);
      playSound('jump');
      setTimeout(() => setIsJumping(false), 250);
    }
  }, [gameState]);

  // Handle keyboard controls
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.code === 'Space') {
        e.preventDefault(); // Prevent default browser behavior (scrolling)
        jump();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [jump]);

  // Handle touch/click for mobile and desktop
  const handleGameAreaClick = () => {
    jump();
  };

  // Handle touch start for mobile (prevent default to avoid scrolling)
  const handleTouchStart = (e) => {
    e.preventDefault();
    jump();
  };

  // Game loop
  useEffect(() => {
    if (gameState !== 'playing') return;

    const updateGame = (timestamp) => {
      if (!lastTimeRef.current) lastTimeRef.current = timestamp;
      const deltaTime = timestamp - lastTimeRef.current;
      lastTimeRef.current = timestamp;

      // Update Hanuman position with smoother movement
      setHanumanPosition(prev => {
        let newY = prev.y + velocity;
        
        // Ground collision with a bit of forgiveness
        const groundLevel = gameDimensions.GAME_HEIGHT - gameDimensions.HANUMAN_HEIGHT;
        if (newY >= groundLevel - 5) { // Small threshold for better feel
          newY = groundLevel;
          // Only reset velocity if falling
          if (velocity > 0) {
            setVelocity(0);
          }
        }
        
        // Ceiling collision
        if (newY <= 0) {
          newY = 0;
          // Only reset velocity if moving up
          if (velocity < 0) {
            setVelocity(0);
          }
        }
        
        return { ...prev, y: newY };
      });

      // Apply gravity with better physics
      setVelocity(prev => {
        // Only apply gravity if not on the ground
        const isOnGround = hanumanPosition.y >= gameDimensions.GAME_HEIGHT - gameDimensions.HANUMAN_HEIGHT - 1;
        if (isOnGround && prev >= 0) {
          return 0; // No gravity when on the ground
        }
        
        // More natural gravity curve
        const gravity = 0.7;
        const newVelocity = prev + gravity;
        
        // Cap falling speed
        return Math.min(newVelocity, 18);
      });

      // Move obstacles
      setObstacles(prev => {
        const updated = prev.map(obs => ({
          ...obs,
          x: obs.x - 5
        })).filter(obs => obs.x > -gameDimensions.OBSTACLE_WIDTH);
        
        // Check for passed obstacles (scoring)
        updated.forEach(obs => {
          if (!obs.scored && obs.x < hanumanPosition.x && hanumanPosition.y < gameDimensions.GAME_HEIGHT - gameDimensions.HANUMAN_HEIGHT) {
            obs.scored = true;
            setScore(s => s + 1);
          }
        });
        
        return updated;
      });

      // Move sanjeevani herbs
      setSanjeevaniHerbs(prev => {
        return prev.map(herb => ({
          ...herb,
          x: herb.x - 5
        })).filter(herb => herb.x > -gameDimensions.SANJEEVANI_WIDTH);
      });

      // Collision detection with obstacles
      const hanumanRect = {
        x: hanumanPosition.x,
        y: hanumanPosition.y,
        width: gameDimensions.HANUMAN_WIDTH,
        height: gameDimensions.HANUMAN_HEIGHT
      };

      for (let obstacle of obstacles) {
        const obstacleRect = {
          x: obstacle.x,
          y: obstacle.y,
          width: gameDimensions.OBSTACLE_WIDTH,
          height: gameDimensions.OBSTACLE_HEIGHT
        };

        if (
          hanumanRect.x < obstacleRect.x + obstacleRect.width &&
          hanumanRect.x + hanumanRect.width > obstacleRect.x &&
          hanumanRect.y < obstacleRect.y + obstacleRect.height &&
          hanumanRect.y + hanumanRect.height > obstacleRect.y
        ) {
          // Collision detected
          playSound('hit');
          setLives(prev => {
            const newLives = prev - 1;
            if (newLives <= 0) {
              setGameState('gameOver');
              playSound('gameOver');
              if (score > highScore) {
                setHighScore(score);
                localStorage.setItem('hanumanHighScore', score);
              }
            }
            return newLives;
          });
          
          // Remove the obstacle after collision
          setObstacles(prev => prev.filter(obs => obs !== obstacle));
          break;
        }
      }

      // Collision detection with sanjeevani herbs
      for (let herb of sanjeevaniHerbs) {
        const herbRect = {
          x: herb.x,
          y: herb.y,
          width: gameDimensions.SANJEEVANI_WIDTH,
          height: gameDimensions.SANJEEVANI_HEIGHT
        };

        if (
          hanumanRect.x < herbRect.x + herbRect.width &&
          hanumanRect.x + hanumanRect.width > herbRect.x &&
          hanumanRect.y < herbRect.y + herbRect.height &&
          hanumanRect.y + hanumanRect.height > herbRect.y
        ) {
          // Collect herb
          playSound('collect');
          setScore(prev => {
            setScoreAnimation(true);
            setTimeout(() => setScoreAnimation(false), 500);
            return prev + 5;
          });
          setSanjeevaniHerbs(prev => prev.filter(h => h !== herb));
          break;
        }
      }

      gameLoopRef.current = requestAnimationFrame(updateGame);
    };

    gameLoopRef.current = requestAnimationFrame(updateGame);
    return () => {
      if (gameLoopRef.current) cancelAnimationFrame(gameLoopRef.current);
    };
  }, [gameState, velocity, hanumanPosition, obstacles, sanjeevaniHerbs, score, highScore, gameDimensions]);

  // Spawn obstacles
  useEffect(() => {
    if (gameState !== 'playing') return;

    const spawnObstacle = () => {
      const minHeight = 50;
      const maxHeight = gameDimensions.GAME_HEIGHT - gameDimensions.OBSTACLE_HEIGHT - 50;
      const y = Math.floor(Math.random() * (maxHeight - minHeight + 1)) + minHeight;
      
      setObstacles(prev => [
        ...prev,
        {
          id: Date.now(),
          x: gameDimensions.GAME_WIDTH,
          y: y,
          scored: false
        }
      ]);

      const nextSpawn = Math.random() * 2000 + 1000; // 1-3 seconds
      obstacleSpawnRef.current = setTimeout(spawnObstacle, nextSpawn);
    };

    obstacleSpawnRef.current = setTimeout(spawnObstacle, 1000);

    return () => {
      if (obstacleSpawnRef.current) clearTimeout(obstacleSpawnRef.current);
    };
  }, [gameState, gameDimensions]);

  // Spawn sanjeevani herbs
  useEffect(() => {
    if (gameState !== 'playing') return;

    const spawnHerb = () => {
      const minY = 100;
      const maxY = gameDimensions.GAME_HEIGHT - gameDimensions.SANJEEVANI_HEIGHT - 100;
      const y = Math.floor(Math.random() * (maxY - minY + 1)) + minY;
      
      setSanjeevaniHerbs(prev => [
        ...prev,
        {
          id: Date.now() + Math.random(),
          x: gameDimensions.GAME_WIDTH,
          y: y
        }
      ]);

      const nextSpawn = Math.random() * 3000 + 2000; // 2-5 seconds
      herbSpawnRef.current = setTimeout(spawnHerb, nextSpawn);
    };

    herbSpawnRef.current = setTimeout(spawnHerb, 2000);

    return () => {
      if (herbSpawnRef.current) clearTimeout(herbSpawnRef.current);
    };
  }, [gameState, gameDimensions]);

  const startGame = () => {
    // Reset dimensions when starting game
    const dimensions = getGameDimensions();
    setGameDimensions(dimensions);
    
    setGameState('playing');
    setHanumanPosition({ 
      x: dimensions.GAME_WIDTH * 0.1, 
      y: dimensions.GAME_HEIGHT * 0.5 
    });
    setVelocity(0);
    setObstacles([]);
    setSanjeevaniHerbs([]);
    setScore(0);
    setLives(3);
    setIsJumping(false);
    setScoreAnimation(false);
    jumpCooldownRef.current = false; // Reset jump cooldown
    lastTimeRef.current = null;
  };

  const resetGame = () => {
    setGameState('menu');
  };

  return (
    <div className="flex flex-col items-center w-full">
      <div className="flex items-center justify-center mb-2">
        <img 
          src="/images/avatars/hanuman-avatar.png" 
          alt="Hanuman" 
          className="w-10 h-10 md:w-12 md:h-12 object-contain mr-3 drop-shadow-md"
          onError={(e) => {
            e.target.src = '/images/avatars/rama.png';
          }}
        />
        <h1 className="text-3xl md:text-4xl font-bold text-orange-600 text-center">Hanuman Leap Challenge</h1>
      </div>
      <p className="text-base md:text-lg text-gray-600 mb-4 md:mb-6 text-center px-4">
        Help Hanuman leap across the ocean to Lanka! Collect Sanjeevani herbs and avoid demons.
      </p>
      
      {/* Game stats */}
      <div className="flex flex-wrap justify-center gap-2 md:gap-4 w-full max-w-2xl mb-4 px-2">
        <motion.div 
          className="bg-orange-100 rounded-full px-3 py-1 md:px-4 md:py-2"
          animate={scoreAnimation ? { scale: [1, 1.2, 1] } : {}}
          transition={{ duration: 0.5 }}
        >
          <span className="font-bold text-orange-600 text-sm md:text-base">Score: {score}</span>
        </motion.div>
        <div className="bg-orange-100 rounded-full px-3 py-1 md:px-4 md:py-2">
          <span className="font-bold text-orange-600 text-sm md:text-base">Lives: {lives}</span>
        </div>
        <div className="bg-orange-100 rounded-full px-3 py-1 md:px-4 md:py-2">
          <span className="font-bold text-orange-600 text-sm md:text-base">High Score: {highScore}</span>
        </div>
      </div>
      
      {/* Game container */}
      <div 
        className="relative bg-gradient-to-b from-sky-300 to-blue-500 border-2 md:border-4 border-orange-500 rounded-xl overflow-hidden shadow-xl w-full max-w-full"
        style={{ 
          width: gameDimensions.GAME_WIDTH, 
          height: gameDimensions.GAME_HEIGHT,
          maxWidth: '95vw'
        }}
        onClick={handleGameAreaClick}
        onTouchStart={handleTouchStart}
      >
        {gameState === 'menu' && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-black bg-opacity-50 z-10 p-2">
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="bg-gradient-to-br from-orange-100 to-yellow-100 rounded-2xl p-4 md:p-8 text-center max-w-md w-full mx-2 border-2 md:border-4 border-orange-500 shadow-2xl"
            >
              <div className="flex justify-center mb-3 md:mb-5">
                <img 
                  src="/images/avatars/hanuman-avatar.png" 
                  alt="Hanuman" 
                  className="w-24 h-24 md:w-32 md:h-32 object-contain drop-shadow-lg"
                  onError={(e) => {
                    e.target.src = '/images/avatars/rama.png';
                  }}
                />
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-orange-600 mb-2 md:mb-4">Hanuman's Great Leap</h2>
              <p className="text-gray-700 text-sm md:text-base mb-2">
                Help Hanuman leap across the ocean to Lanka!
              </p>
              <p className="text-gray-700 text-sm md:text-base mb-4 md:mb-6">
                Press SPACE or tap to jump. Collect Sanjeevani herbs and avoid demons!
              </p>
              <button
                onClick={startGame}
                className="bg-orange-500 hover:bg-orange-600 text-white font-bold py-2 md:py-3 px-6 md:px-8 rounded-full text-lg md:text-xl transition-colors shadow-lg mb-4 md:mb-6 flex items-center justify-center mx-auto"
              >
                <span>Start Leap</span>
                <span className="ml-2">🚀</span>
              </button>
              <div className="bg-white rounded-xl p-3 md:p-4 mb-3 md:mb-4 shadow-md">
                <h3 className="font-bold text-orange-600 mb-2 text-sm md:text-base">How to Play:</h3>
                <ul className="text-xs md:text-sm text-gray-600 list-disc pl-4 md:pl-5 space-y-1 text-left">
                  <li>Press <span className="font-bold">SPACEBAR</span> or <span className="font-bold">TAP</span> to make Hanuman jump</li>
                  <li>Collect golden <span className="text-yellow-600 font-bold">Sanjeevani herbs</span> for extra points</li>
                  <li>Avoid red <span className="text-red-600 font-bold">demon obstacles</span></li>
                  <li>You have <span className="font-bold">3 lives</span></li>
                </ul>
              </div>
              <div className="text-gray-500 text-xs md:text-sm">
                <p>Based on the epic tale from Ramayana</p>
              </div>
            </motion.div>
          </div>
        )}
        
        {gameState === 'gameOver' && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-black bg-opacity-50 z-10 p-2">
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="bg-white rounded-2xl p-4 md:p-8 text-center max-w-md w-full mx-2"
            >
              <h2 className="text-2xl md:text-3xl font-bold text-red-600 mb-2">Game Over!</h2>
              <div className="flex justify-center mb-3 md:mb-5">
                <img 
                  src="/images/avatars/hanuman-avatar.png" 
                  alt="Hanuman" 
                  className="w-20 h-20 md:w-24 md:h-24 object-contain drop-shadow-lg"
                  onError={(e) => {
                    e.target.src = '/images/avatars/rama.png';
                  }}
                />
              </div>
              <p className="text-xl md:text-2xl text-gray-700 mb-2">Final Score: <span className="font-bold text-orange-600">{score}</span></p>
              {score === parseInt(highScore) && score > 0 && (
                <div className="mb-3 md:mb-4">
                  <p className="text-lg md:text-xl text-yellow-600 font-bold">🎉 New High Score! 🎉</p>
                  <p className="text-base md:text-lg text-yellow-500">You're a true devotee of Hanuman!</p>
                  <div className="flex justify-center mt-1 md:mt-2">
                    {[...Array(5)].map((_, i) => (
                      <motion.div
                        key={i}
                        animate={{ 
                          y: [0, -15, 0],
                          rotate: [0, 360]
                        }}
                        transition={{ 
                          duration: 0.5,
                          delay: i * 0.1,
                          repeat: 3
                        }}
                        className="text-xl md:text-2xl mx-1"
                      >
                        🎊
                      </motion.div>
                    ))}
                  </div>
                </div>
              )}
              <div className="bg-orange-50 rounded-xl p-3 md:p-4 mb-4 md:mb-6">
                <h3 className="font-bold text-orange-600 mb-2 text-sm md:text-base">Hanuman's Blessing:</h3>
                <p className="text-gray-700 italic text-xs md:text-sm">
                  "Courage and devotion will lead you to victory. Try again, young warrior!"
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 md:gap-4 justify-center">
                <button
                  onClick={startGame}
                  className="bg-orange-500 hover:bg-orange-600 text-white font-bold py-2 md:py-3 px-4 md:px-6 rounded-full transition-colors shadow-lg flex items-center justify-center text-sm md:text-base"
                >
                  <span>Play Again</span>
                </button>
                <button
                  onClick={resetGame}
                  className="bg-gray-500 hover:bg-gray-600 text-white font-bold py-2 md:py-3 px-4 md:px-6 rounded-full transition-colors shadow-lg flex items-center justify-center text-sm md:text-base"
                >
                  <span>Main Menu</span>
                </button>
              </div>
            </motion.div>
          </div>
        )}
        
        {/* Ocean waves effect */}
        <div className="absolute bottom-0 w-full h-10 md:h-20 bg-gradient-to-t from-blue-700 to-transparent"></div>
        
        {/* Hanuman character */}
        {gameState !== 'menu' && (
          <motion.div
            className="absolute"
            style={{
              left: hanumanPosition.x,
              top: hanumanPosition.y,
              width: gameDimensions.HANUMAN_WIDTH,
              height: gameDimensions.HANUMAN_HEIGHT
            }}
            animate={{ 
              rotate: isJumping ? [-10, 10, -10] : 0,
              scale: isJumping ? [1, 1.1, 1] : 1
            }}
            transition={{ 
              duration: 0.3, 
              repeat: isJumping ? Infinity : 0,
              scale: { duration: 0.1 }
            }}
          >
            <div className="w-full h-full flex items-center justify-center relative">
              <img 
                src="/images/avatars/hanuman-avatar.png" 
                alt="Hanuman" 
                className="w-full h-full object-contain drop-shadow-lg"
                onError={(e) => {
                  e.target.src = '/images/avatars/rama.png';
                }}
              />
              {isJumping && (
                <div className="absolute -top-6 md:-top-8 text-yellow-400 font-bold text-xl md:text-2xl animate-bounce">
                  🚀
                </div>
              )}
            </div>
          </motion.div>
        )}
        
        {/* Obstacles (demons) */}
        {obstacles.map(obstacle => (
          <motion.div
            key={obstacle.id}
            className="absolute flex items-center justify-center drop-shadow-md"
            style={{
              left: obstacle.x,
              top: obstacle.y,
              width: gameDimensions.OBSTACLE_WIDTH,
              height: gameDimensions.OBSTACLE_HEIGHT
            }}
            whileHover={{ scale: 1.1 }}
          >
            <img 
              src="/images/avatars/asura-avatar.png" 
              alt="Asura Demon" 
              className="w-full h-full object-contain"
              onError={(e) => {
                // Fallback to a red box if image fails to load
                e.target.style.display = 'none';
                e.target.parentElement.innerHTML = '<div class="w-full h-full bg-red-600 rounded-lg border-2 border-red-800 flex items-center justify-center"><div class="text-4xl">👹</div></div>';
              }}
            />
          </motion.div>
        ))}
        
        {/* Sanjeevani herbs */}
        {sanjeevaniHerbs.map(herb => (
          <motion.div
            key={herb.id}
            className="absolute bg-yellow-400 rounded-full flex items-center justify-center border-2 border-yellow-600 shadow-lg"
            style={{
              left: herb.x,
              top: herb.y,
              width: gameDimensions.SANJEEVANI_WIDTH,
              height: gameDimensions.SANJEEVANI_HEIGHT
            }}
            animate={{ 
              y: [0, -10, 0],
              rotate: [0, 360]
            }}
            transition={{ 
              y: { duration: 1, repeat: Infinity },
              rotate: { duration: 3, repeat: Infinity }
            }}
          >
            <div className="text-xl md:text-2xl">🌿</div>
          </motion.div>
        ))}

        {/* Ground */}
        <div className="absolute bottom-0 w-full h-3 md:h-4 bg-gradient-to-t from-green-700 to-green-500"></div>
      </div>
      
      {/* Game instructions */}
      <div className="mt-4 md:mt-6 text-center max-w-2xl px-2">
        <div className="bg-orange-100 rounded-xl p-3 md:p-4 mb-3 md:mb-4">
          <p className="text-gray-700 mb-1 md:mb-2 text-sm md:text-base">
            <span className="font-bold text-orange-600">Controls:</span> Press SPACEBAR or TAP/CLICK to make Hanuman jump!
          </p>
          <p className="text-gray-600 text-xs md:text-sm">
            Press repeatedly for multiple jumps in the air!
          </p>
        </div>
        <div className="flex flex-wrap justify-center gap-3 md:gap-8 mt-3 md:mt-4">
          <div className="flex items-center bg-white rounded-full px-3 py-1 md:px-4 md:py-2 shadow">
            <div className="w-6 h-6 md:w-8 md:h-8 bg-yellow-400 rounded-full flex items-center justify-center mr-2 border border-yellow-600">
              <div className="text-xs">🌿</div>
            </div>
            <span className="text-gray-700 text-xs md:text-sm">Sanjeevani Herb (+5 points)</span>
          </div>
          <div className="flex items-center bg-white rounded-full px-3 py-1 md:px-4 md:py-2 shadow">
            <div className="w-6 h-6 md:w-8 md:h-8 bg-red-600 rounded-lg flex items-center justify-center mr-2 border border-red-800">
              <div className="text-xs">👹</div>
            </div>
            <span className="text-gray-700 text-xs md:text-sm">Demon Obstacle (-1 life)</span>
          </div>
        </div>
      </div>
      
      {/* Story section */}
      <div className="mt-6 md:mt-8 bg-orange-50 rounded-2xl p-4 md:p-6 max-w-2xl w-full mx-2">
        <h3 className="text-xl md:text-2xl font-bold text-orange-600 mb-2 md:mb-3">The Story of Hanuman's Leap</h3>
        <p className="text-gray-700 mb-2 md:mb-3 text-sm md:text-base">
          When Lakshmana was gravely wounded in battle, only the Sanjeevani herb could save him. 
          Hanuman was sent to fetch it from the Himalayas, but he couldn't identify the exact herb.
        </p>
        <p className="text-gray-700 text-sm md:text-base">
          In his wisdom, he decided to carry the entire mountain back to Lanka! 
          His incredible leap across the ocean demonstrates his unwavering devotion to Lord Rama.
        </p>
      </div>
    </div>
  );
};

export default HanumanLeapChallenge;